/**
 * Vevor-Style Mega Menu JavaScript
 * Handles hover interactions and dynamic content updates
 */

class VevorMegaMenu {
  constructor() {
    this.megaMenu = document.querySelector('.vevor-mega-menu');
    this.sidebar = document.querySelector('.vevor-mega-sidebar');
    this.contentPanel = document.querySelector('.vevor-mega-content-panel');
    this.contentTitle = document.querySelector('.vevor-mega-content-title');
    this.contentDescription = document.querySelector('.vevor-mega-content-description');
    this.seeAllLink = document.querySelector('.vevor-mega-see-all');
    this.subcategoriesContainer = document.querySelector('.vevor-mega-subcategories');
    this.imageArea = document.querySelector('.vevor-mega-image-area');

    this.categories = [];
    this.activeCategory = null;
    this.hoverTimeout = null;
    this.taxonomyUtility = null;

    this.init();
  }
  
  init() {
    if (!this.megaMenu || !this.sidebar || !this.contentPanel) {
      console.warn('Vevor mega menu elements not found');
      return;
    }

    // Initialize taxonomy utility
    this.initTaxonomyUtility();

    this.loadCategoryData();
    this.bindEvents();

    // Set first category as default if available
    if (this.categories.length > 0) {
      this.setActiveCategory(this.categories[0]);
    }
  }

  initTaxonomyUtility() {
    // Wait for taxonomy utility to be available
    if (window.taxonomyUtility) {
      this.taxonomyUtility = window.taxonomyUtility;
    } else if (window.TaxonomyUtility) {
      this.taxonomyUtility = new window.TaxonomyUtility();
    } else {
      // Listen for the taxonomyUtilityReady event
      window.addEventListener('taxonomyUtilityReady', () => {
        this.taxonomyUtility = window.taxonomyUtility;
        // Re-render the active category if one is set
        if (this.activeCategory) {
          this.renderCategoryContent(this.activeCategory);
        }
      }, { once: true });
    }
  }
  
  loadCategoryData() {
    const categoryElements = this.sidebar.querySelectorAll('.vevor-mega-category');

    categoryElements.forEach((element, index) => {
      const dataScript = element.querySelector('.category-data');
      if (dataScript) {
        try {
          const categoryData = JSON.parse(dataScript.textContent);
          categoryData.element = element;
          categoryData.index = index;
          this.categories.push(categoryData);
        } catch (error) {
          // Silently skip invalid JSON
        }
      }
    });
  }
  
  bindEvents() {
    // Category hover events
    this.categories.forEach(category => {
      category.element.addEventListener('mouseenter', () => {
        this.handleCategoryHover(category);
      });
      
      category.element.addEventListener('mouseleave', () => {
        this.handleCategoryLeave(category);
      });
      
      category.element.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleCategoryClick(category);
      });
    });
    
    // Mega menu hover events to prevent closing
    this.megaMenu.addEventListener('mouseenter', () => {
      this.clearHoverTimeout();
    });
    
    this.megaMenu.addEventListener('mouseleave', () => {
      this.scheduleClose();
    });
  }
  
  handleCategoryHover(category) {
    this.clearHoverTimeout();
    this.setActiveCategory(category);
  }
  
  handleCategoryLeave(category) {
    // Don't immediately clear - let mouse move to content panel
    this.hoverTimeout = setTimeout(() => {
      // Only clear if mouse isn't in content panel
      if (!this.contentPanel.matches(':hover')) {
        this.clearActiveCategory();
      }
    }, 100);
  }
  
  handleCategoryClick(category) {
    // Navigate to category page
    if (category.url) {
      window.location.href = category.url;
    }
  }
  
  setActiveCategory(category) {
    if (this.activeCategory === category) return;
    
    // Remove active class from all categories
    this.categories.forEach(cat => {
      cat.element.classList.remove('active');
    });
    
    // Set new active category
    this.activeCategory = category;
    category.element.classList.add('active');
    
    // Update content panel
    this.updateContentPanel(category);
  }
  
  clearActiveCategory() {
    if (this.activeCategory) {
      this.activeCategory.element.classList.remove('active');
      this.activeCategory = null;
    }
    
    // Show placeholder content
    this.showPlaceholder();
  }
  
  updateContentPanel(category) {
    // Update title and see all link
    this.contentTitle.textContent = category.title;

    const categoryDescription = this.isCabEnclosureKits(category)
      ? 'Compare model-specific polycarbonate cab kits and removable all-weather nylon enclosures, then shop by machine make.'
      : category.description;

    // Update description if available
    if (categoryDescription && categoryDescription.trim()) {
      // Truncate description if too long (max ~140 characters to avoid wrapping)
      const truncatedDescription = this.truncateDescription(categoryDescription, 140);
      this.contentDescription.textContent = truncatedDescription;
      this.contentDescription.style.display = 'block';
    } else {
      this.contentDescription.style.display = 'none';
    }

    this.seeAllLink.href = category.url;
    this.seeAllLink.style.display = 'inline-block';

    // Clear existing subcategories
    this.subcategoriesContainer.innerHTML = '';

    // Try to use taxonomy system first
    if (this.taxonomyUtility && this.tryRenderTaxonomies(category)) {
      // Taxonomies rendered successfully
    } else {
      // Fallback to traditional subcategories
      this.renderTraditionalSubcategories(category);
    }

    // Update collection image
    this.updateCollectionImage(category);
  }

  tryRenderTaxonomies(category) {
    try {
      // Get the global taxonomy data from the script tag
      // Use more specific selector to find the category in the mega menu sidebar
      const categoryElement = document.querySelector(`.vevor-mega-sidebar .vevor-mega-category[data-category="${category.handle}"]`);
      if (!categoryElement) {
        return false;
      }

      const scriptElement = categoryElement.querySelector('.category-data');
      if (!scriptElement) {
        return false;
      }

      const rawScriptData = JSON.parse(scriptElement.textContent);

      // The enclosure parent combines explicit shopper-intent links with make
      // links, so it must not be flattened by the generic taxonomy renderer.
      if (this.isCabEnclosureKits(category)) {
        this.renderCabEnclosureSubcategories(category);
        return true;
      }

      // Check if this collection has taxonomies in the global taxonomy data
      if (rawScriptData.taxonomies && rawScriptData.taxonomies.length > 0) {
        // Check if this is a parent collection (has taxonomies but isn't referenced in any taxonomy)
        const isParentCollection = !rawScriptData.taxonomies.some(taxonomy =>
          taxonomy.collections && taxonomy.collections.some(collection =>
            collection.handle === category.handle
          )
        );

        if (isParentCollection) {
          // Create taxonomy data structure expected by the utility
          const taxonomyData = {
            hastaxonomies: true,
            taxonomies: rawScriptData.taxonomies,
            fallbackItems: []
          };

          // Use taxonomy utility with mega menu specific styling
          this.taxonomyUtility.renderTaxonomyColumns(this.subcategoriesContainer, taxonomyData, {
            columnClass: 'vevor-mega-taxonomy-column',
            headerClass: 'vevor-mega-taxonomy-header',
            itemClass: 'vevor-mega-subcategory',
            linkClass: 'vevor-mega-subcategory-link'
          });

          // TEMPORARILY DISABLED - "Shop by" label removed per user request
          // Add "Shop by" label after taxonomy columns are rendered
          // this.addShopByLabel();

          // Add taxonomy mode class to container for styling
          this.subcategoriesContainer.classList.add('taxonomy-mode');
          return true;
        } else {
          // For other collections, find taxonomies that include this collection
          const relevantTaxonomies = rawScriptData.taxonomies.filter(taxonomy => {
            return taxonomy.collections && taxonomy.collections.some(collection =>
              collection.handle === category.handle
            );
          });

          if (relevantTaxonomies.length > 0) {
            // Create taxonomy data structure expected by the utility
            const taxonomyData = {
              hastaxonomies: true,
              taxonomies: relevantTaxonomies,
              fallbackItems: []
            };

            // Use taxonomy utility with mega menu specific styling
            this.taxonomyUtility.renderTaxonomyColumns(this.subcategoriesContainer, taxonomyData, {
              columnClass: 'vevor-mega-taxonomy-column',
              headerClass: 'vevor-mega-taxonomy-header',
              itemClass: 'vevor-mega-subcategory',
              linkClass: 'vevor-mega-subcategory-link'
            });

            // TEMPORARILY DISABLED - "Shop by" label removed per user request
            // Add "Shop by" label after taxonomy columns are rendered
            // this.addShopByLabel();

            // Add taxonomy mode class to container for styling
            this.subcategoriesContainer.classList.add('taxonomy-mode');
            return true;
          }
        }
      }
    } catch (error) {
      console.warn('Error rendering taxonomies:', error);
    }

    return false;
  }

  // TEMPORARILY DISABLED - "Shop by" label removed per user request
  // TODO: Re-enable this if we want to show "Shop by:" labels again
  /*
  addShopByLabel() {
    // Add "Shop by" label at the top of the subcategories container
    const shopByLabel = document.createElement('div');
    shopByLabel.className = 'vevor-mega-shop-by-label';
    shopByLabel.textContent = 'Shop by:';
    // Prepend to the beginning of the container
    this.subcategoriesContainer.insertBefore(shopByLabel, this.subcategoriesContainer.firstChild);
  }
  */

  renderTraditionalSubcategories(category) {
    // Remove taxonomy mode class
    this.subcategoriesContainer.classList.remove('taxonomy-mode');

    const isCabEnclosureCategory = this.isCabEnclosureKits(category);

    if (isCabEnclosureCategory) {
      this.renderCabEnclosureSubcategories(category);
    } else {
      // Add subcategories the traditional way for other categories
      if (category.subcategories && category.subcategories.length > 0) {
        category.subcategories.forEach(subcategory => {
          const subcategoryElement = this.createSubcategoryElement(subcategory);
          this.subcategoriesContainer.appendChild(subcategoryElement);
        });
      } else {
        this.showNoSubcategories();
      }
    }
  }
  
  createSubcategoryElement(subcategory) {
    const element = document.createElement('div');
    element.className = 'vevor-mega-subcategory';

    const link = document.createElement('a');
    link.href = subcategory.url;
    link.textContent = subcategory.title;

    element.appendChild(link);
    return element;
  }

  isCabEnclosureKits(category) {
    const categoryHandle = (category.handle || '').toLowerCase();
    const categoryTitle = (category.title || '').trim().toLowerCase();

    return categoryHandle === 'skid-steer-enclosures' ||
      categoryTitle === 'cab enclosure kits';
  }

  getSubcategoryHandle(subcategory) {
    const url = subcategory.url || '';
    const collectionMatch = url.match(/\/collections\/([^/?#]+)/i);
    if (collectionMatch) return collectionMatch[1].toLowerCase();

    return (subcategory.handle || '').trim().toLowerCase();
  }

  renderCabEnclosureSubcategories(category) {
    if (!category.subcategories || category.subcategories.length === 0) {
      this.showNoSubcategories();
      return;
    }

    const typeHandles = new Set([
      'skid-steer-enclosures',
      'pro-series-enclosure-kits',
      'nylon-cab-enclosures'
    ]);
    const enclosureTypes = category.subcategories.filter(subcategory =>
      typeHandles.has(this.getSubcategoryHandle(subcategory))
    );
    const makes = category.subcategories.filter(subcategory =>
      !typeHandles.has(this.getSubcategoryHandle(subcategory))
    );

    this.renderCabEnclosureGroup('TYPE', enclosureTypes);
    this.renderCabEnclosureGroup('MAKE', makes);
    this.subcategoriesContainer.classList.add('taxonomy-mode');
  }

  renderCabEnclosureGroup(groupTitle, subcategories) {
    if (!subcategories || subcategories.length === 0) return;

    const maxItemsPerSubColumn = 8;
    const column = document.createElement('div');
    column.className = 'vevor-mega-taxonomy-column';

    const header = document.createElement('h4');
    header.className = 'vevor-mega-taxonomy-header';
    header.textContent = groupTitle;
    column.appendChild(header);

    const needsMultipleColumns = subcategories.length > maxItemsPerSubColumn;

    if (needsMultipleColumns) {
      const subColumnsContainer = document.createElement('div');
      subColumnsContainer.className = 'taxonomy-sub-columns';

      const chunks = [];
      for (let i = 0; i < subcategories.length; i += maxItemsPerSubColumn) {
        chunks.push(subcategories.slice(i, i + maxItemsPerSubColumn));
      }

      chunks.forEach(chunk => {
        const subColumn = document.createElement('div');
        subColumn.className = 'taxonomy-sub-column';

        chunk.forEach(subcategory => {
          const item = document.createElement('div');
          item.className = 'vevor-mega-subcategory';

          const link = document.createElement('a');
          link.href = subcategory.url;
          link.className = 'vevor-mega-subcategory-link';
          link.textContent = subcategory.title;

          item.appendChild(link);
          subColumn.appendChild(item);
        });

        subColumnsContainer.appendChild(subColumn);
      });

      column.classList.add('has-sub-columns');
      column.appendChild(subColumnsContainer);
    } else {
      subcategories.forEach(subcategory => {
        const item = document.createElement('div');
        item.className = 'vevor-mega-subcategory';

        const link = document.createElement('a');
        link.href = subcategory.url;
        link.className = 'vevor-mega-subcategory-link';
        link.textContent = subcategory.title;

        item.appendChild(link);
        column.appendChild(item);
      });
    }

    this.subcategoriesContainer.appendChild(column);
  }
  
  updateCollectionImage(category) {
    if (!this.imageArea) return;

    // TEMPORARILY DISABLED - Collection images in mega menu
    // TODO: Re-enable this when we want to show collection images again
    /*
    // Check if category has an image
    if (category.image && category.image.url) {
      this.imageArea.innerHTML = `
        <img src="${category.image.url}" alt="${category.image.alt || category.title}"
             style="width: 100%; height: 200px; object-fit: cover; border-radius: 4px;">
      `;
      this.imageArea.style.display = 'block';
    } else {
      // Hide image area if no image
      this.imageArea.style.display = 'none';
    }
    */

    // Always hide image area for now
    this.imageArea.style.display = 'none';
  }

  showPlaceholder() {
    this.contentTitle.textContent = 'Select a category';
    this.contentDescription.style.display = 'none';
    this.seeAllLink.style.display = 'none';

    this.subcategoriesContainer.innerHTML = `
      <div class="vevor-mega-placeholder">
        <p>Hover over a category to see subcategories</p>
      </div>
    `;

    // TEMPORARILY DISABLED - Image area is always hidden now
    // Hide image area in placeholder state
    if (this.imageArea) {
      this.imageArea.style.display = 'none';
    }
  }
  
  showNoSubcategories() {
    this.subcategoriesContainer.innerHTML = `
      <div class="vevor-mega-placeholder">
        <p>No subcategories available</p>
      </div>
    `;
  }
  
  clearHoverTimeout() {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
  }
  
  scheduleClose() {
    this.hoverTimeout = setTimeout(() => {
      this.clearActiveCategory();
    }, 200);
  }
  
  // Public methods for external control
  show() {
    this.megaMenu.classList.add('active');
  }
  
  hide() {
    this.megaMenu.classList.remove('active');
    this.clearActiveCategory();
  }
  
  toggle() {
    if (this.megaMenu.classList.contains('active')) {
      this.hide();
    } else {
      this.show();
    }
  }

  /**
   * Truncate description text to fit within 2 lines
   * @param {string} text - The text to truncate
   * @param {number} maxLength - Maximum character length
   * @returns {string} - Truncated text
   */
  truncateDescription(text, maxLength = 140) {
    if (!text || text.length <= maxLength) {
      return text;
    }

    // Find the last space before the max length to avoid cutting words
    const truncated = text.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');

    if (lastSpace > maxLength * 0.8) { // Only use last space if it's not too early
      return truncated.substring(0, lastSpace) + '...';
    } else {
      return truncated + '...';
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.vevorMegaMenu = new VevorMegaMenu();
});

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = VevorMegaMenu;
}
